/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CostCenterFinderDialogConfig,
  costCenterFinderDialogRoute,
  CostCenterFinderListItem,
  FormActionPipelines,
  FormStateBuilder,
  predecessorValueFormatter,
  ReportService,
} from '@gh/builder-shared';
import { BuilderPermission, ScheduleTaskType, ScheduleType } from '@gh/builder-shared-data';
import {
  cellValueFormatter,
  code3Formatter,
  createDefaultGridModel,
  dictionaryValueFormatter,
  ROW_GROUP_CLASS,
} from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  EnumDictionary,
  FormStateSnapshot,
  getAllNewSelectedEntities,
  getFormStateSnapshotValue,
  getRouteInitData,
  getRouteStaticData,
  HelpID,
  isRouteForCreationMode,
  LocalizationBusinessService,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  Confirmation,
  DialogService,
  FormMode,
  noAnswer,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import { buildEditableCheckboxColumn, GridDefinition, GridDefinitionFactory, GridLayout } from '@gh/core-ui-grid';
import { openInNewWindow } from '@gh/core-util';
import { EMPTY$, merge$, of$ } from '@gh/rx';
import { filter$, map$, switchMap$, tap$ } from '@gh/rx/operators';

import { ValueFormatterParams } from 'ag-grid-community';
import { getScheduleTaskId, isScheduleTaskStart, scheduleTaskFieldState } from '@gh/builder-shared-gantt-chart';
import { each, map, max, size } from 'lodash';
import { Observable } from 'rxjs';

import { SchedulingScheduleTemplateBusinessService } from '../../resources/scheduling-schedule-template-business.service';
import {
  createFromCostCenterFinderList,
  ScheduleTemplateAelEntityType,
  ScheduleTemplateDetails,
  ScheduleTemplateDetailsInfo,
  ScheduleTemplateDetailsSaveFlowState,
  ScheduleTemplateTask,
} from '../../resources/scheduling-schedule-template.model';
import { SchedulingScheduleTemplateDetailsFormState } from './scheduling-schedule-template-details-form.service';

@Component({
  selector: 'gh-scheduling-schedule-template-details',
  template: require('./scheduling-schedule-template-details.component.html'),
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    SchedulingScheduleTemplateDetailsFormState,
  ],
})
export class SchedulingScheduleTemplateDetailsComponent extends AbstractFormComponent<ScheduleTemplateDetails>
  implements OnInit {

  gridDefinition: GridDefinition;
  scheduleTemplateTaskType: EnumDictionary<ScheduleTaskType>;
  scheduleTemplateType: ScheduleType;
  scheduleTemplate: ScheduleTemplateDetails;
  tasksNumber: number;
  formMode: FormMode = FormMode.EditMode;
  pageTitle: string;
  reportUrl: string;

  get helpId(): HelpID {
    return {
      [ScheduleType.Construction]: 'setup/schedules/constructions/details',
      [ScheduleType.Sales]: 'setup/schedules/sales/details',
      [ScheduleType.PreConstruction]: 'setup/schedules/pre-constructions/details',
      [ScheduleType.Maintenance]: 'setup/schedules/warranty/details',
    }[this.scheduleTemplateType];
  }

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private translationService: TranslationService,
              private reportService: ReportService,
              private userContext: BuilderUserContext,
              private waitingService: WaitingService,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private localizationBusinessService: LocalizationBusinessService,
              private notificationService: NotificationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private formStateBuilder: FormStateBuilder,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              public form: SchedulingScheduleTemplateDetailsFormState,
              private businessService: SchedulingScheduleTemplateBusinessService) {
    super();
  }

  /**
   * @returns {ActionFlow<ScheduleTemplateDetailsSaveFlowState>}
   */
  get completeFlow(): ActionFlow<ScheduleTemplateDetailsSaveFlowState> {
    return this.pipelines.complete<ScheduleTemplateDetailsSaveFlowState>({
      validateForm: this.commonActions.validateWithEffect(
        () => this.form.isTopFormInvalid,
        () => this.form.highlightInfoSnapshot()),
      execute: this.commonActions.async((state) =>
        this.businessService.save(this.form.value).pipe(
          switchMap$(({ templateId }: ScheduleTemplateDetails) => this.businessService.get(<ID>templateId)),
          map$((saveResponse: ScheduleTemplateDetails) => ({ ...state, saveResponse })))),
      finish: ({ saveResponse }) => {
        if (this.form.createMode && saveResponse) {
          this.form.resetDirty();
          this.navigateToScheduleTemplateEdit(<ID>saveResponse.templateId);
        } else {
          this.form.build(saveResponse!, this.formMode); // tslint:disable-line:no-non-null-assertion
          this.updatePageTitle();
        }
      },
    });
  }

  ngOnInit(): void {
    const {
      scheduleTemplate,
      scheduleTemplateTaskType,
      gridLayout,
      reportUrl,
    } = getRouteInitData(this.route);
    const { type: scheduleTemplateType } = getRouteStaticData(this.route);
    this.scheduleTemplateTaskType = scheduleTemplateTaskType;
    this.scheduleTemplateType = scheduleTemplateType;
    this.scheduleTemplate = scheduleTemplate;
    this.reportUrl = reportUrl;
    if (isRouteForCreationMode(this.route)) {
      this.formMode = FormMode.CreateMode;
    } else if (!this.hasScheduleTemplatePermission('EDIT')) {
      this.formMode = FormMode.ViewMode;
    }
    this.gridDefinition = this.buildGridDefinition(gridLayout);
    this.tasksNumber = max(map(scheduleTemplate.tasks,
                               ({ taskNumber }: ScheduleTemplateTask) => taskNumber)) || 0;

    this.form.build(scheduleTemplate, this.formMode);
    this.form.recalculate$$.pipe(
      tap$(() => this.form.setPending(true)),
      switchMap$(() => this.businessService.recalculate(this.form.submitValue)))
      .subscribe(
        (details: ScheduleTemplateDetails) => {
          this.form.setPending(false);
          this.form.setValue(details);
        },
        (error) => {
          this.form.setPending(false);
          this.notificationService.processAll(error);
        });

    this.updatePageTitle();
  }

  onDelete(task: ScheduleTemplateTask): void {
    const message = task.type === ScheduleTaskType.Summary
      ? 'msg_sch_sum_task_delete'
      : 'msg_sch_template_task_delete';
    this.dialogService.confirmation(yesNoConfirmation(message)).pipe(
      filter$(yesAnswer),
      switchMap$(() => {
        if (task.type === ScheduleTaskType.CostCenter && task.templateTaskId) {
          return this.businessService.checkDelete(this.form.submitValue, task.templateTaskId);
        } else {
          return of$(void 0);
        }
      }))
      .subscribe(
        () => {
          this.form.deleteTask(task);
          this.businessService
            .recalculate(this.form.submitValue).pipe(
            (this.waitingService.operator()))
            .subscribe(
              (scheduleTemplateDetails: ScheduleTemplateDetails) =>
                this.form.build(scheduleTemplateDetails, this.formMode, false),
              this.notificationService.processAll);
        },
        this.notificationService.processAll);
  }

  onTopFormDataChange(snapshot: FormStateSnapshot<ScheduleTemplateDetailsInfo>): void {
    this.form.updateInfoSnapshot(snapshot);
  }

  onCostCenterAdd(): void {
    this.dialogService.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
      multiselection: true,
      selection: true,
      filter: {
        isInJobSchedule: true,
      },
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((costCenterList: CostCenterFinderListItem[]) => {
        const tasks = map(costCenterList, createFromCostCenterFinderList(++this.tasksNumber));
        this.addTasks(tasks);
      });
  }

  onEdit({ templateTaskId, taskNumber, isNew }: ScheduleTemplateTask): void {
    this.confirmNavigation(false).subscribe((confirmation: Confirmation) => {
      if (confirmation === Confirmation.No) {
        if (isNew) {
          this.notificationService.error('msg_sch_no_task');
        } else {
          this.form.resetDirty();
          this.navigateToScheduleTemplateTaskEdit(<ID>this.form.templateId, <ID>templateTaskId);
        }
      } else if (confirmation === Confirmation.Yes) {
        const { templateTaskId: taskId } = this.form.findTemplateTaskByTaskNumber(<number>taskNumber);
        this.navigateToScheduleTemplateTaskEdit(<ID>this.form.templateId, <ID>taskId);
      }
    });
  }

  canBeOpened(task: ScheduleTemplateTask): boolean {
    return !isScheduleTaskStart(task);
  }

  hasScheduleTemplatePermission(permission: string): boolean {
    switch (this.scheduleTemplateType) {
      case ScheduleType.Construction:
        return this.userContext.hasPermission(BuilderPermission.Setup.Schedules.ConstructionTemplates[permission]);
      case ScheduleType.Sales:
        return this.userContext.hasPermission(BuilderPermission.Setup.Schedules.SalesTemplates[permission]);
      case ScheduleType.PreConstruction:
        return this.userContext.hasPermission(BuilderPermission.Setup.Schedules.PreConstructionTemplates[permission]);
      case ScheduleType.Maintenance:
        return this.userContext.hasPermission(BuilderPermission.Setup.Schedules.MaintenanceTemplates[permission]);
      default:
        return false;
    }
  }

  goToAel(): void {
    const params = {
      [ScheduleType.Construction]: {
        entityId: this.form.templateId || undefined,
        entityType: ScheduleTemplateAelEntityType.Construction,
        label: 'lbl_construction_sch_template',
      },
      [ScheduleType.Sales]: {
        entityId: this.form.templateId || undefined,
        entityType: ScheduleTemplateAelEntityType.Sales,
        label: 'lbl_sales_sch_template',
      },
      [ScheduleType.PreConstruction]: {
        entityId: this.form.templateId || undefined,
        entityType: ScheduleTemplateAelEntityType.PreConstruction,
        label: 'lbl_pre_construction_sch_template',
      },
      [ScheduleType.Maintenance]: {
        entityId: this.form.templateId || undefined,
        entityType: ScheduleTemplateAelEntityType.Maintenance,
        label: 'lbl_maintenance_sch_template',
      },
    };
    this.navigationService.goToAel(params[this.scheduleTemplateType]);
  }

  goToGanttChart(): void {
    this.confirmNavigation().subscribe(() => {
      this.router.navigateToChild(['/setup/scheduling/schedule-templates', {
        [ScheduleType.Construction]: 'construction',
        [ScheduleType.Sales]: 'sales',
        [ScheduleType.PreConstruction]: 'pre-construction',
        [ScheduleType.Maintenance]: 'maintenance',
      }[this.scheduleTemplateType], this.form.templateId, 'gantt-chart']);
    });
  }

  onOpenReport(): void {
    if (this.form.dirty) {
      this.notificationService.error('msg_save_first');
    } else if (this.scheduleTemplate.templateId) {
      openInNewWindow(this.reportUrl);
    }
  }

  isConstructionType(): boolean {
    return this.scheduleTemplateType === ScheduleType.Construction;
  }

  private navigateToScheduleTemplateTaskEdit(templateId: ID, templateTaskId: ID): void {
    switch (this.scheduleTemplateType) {
      case ScheduleType.Construction:
        this.router.navigateToChild([
          'setup', 'scheduling', 'schedule-templates', 'construction', templateId, 'tasks', templateTaskId]);
        break;
      case ScheduleType.Sales:
        this.router.navigateToChild([
          'setup', 'scheduling', 'schedule-templates', 'sales', templateId, 'tasks', templateTaskId]);
        break;
      case ScheduleType.PreConstruction:
        this.router.navigateToChild([
          'setup', 'scheduling', 'schedule-templates', 'pre-construction', templateId, 'tasks', templateTaskId]);
        break;
      case ScheduleType.Maintenance:
        this.router.navigateToChild([
          'setup', 'scheduling', 'schedule-templates', 'maintenance', templateId, 'tasks', templateTaskId]);
        break;
      default:
        break;
    }
  }

  private navigateToScheduleTemplateEdit(templateId: ID): void {
    switch (this.scheduleTemplateType) {
      case ScheduleType.Construction:
        this.router.switchTo(['setup', 'scheduling', 'schedule-templates', 'construction', templateId]);
        break;
      case ScheduleType.Sales:
        this.router.switchTo(['setup', 'scheduling', 'schedule-templates', 'sales', templateId]);
        break;
      case ScheduleType.PreConstruction:
        this.router.switchTo(['setup', 'scheduling', 'schedule-templates', 'pre-construction', templateId]);
        break;
      case ScheduleType.Maintenance:
        this.router.switchTo(['setup', 'scheduling', 'schedule-templates', 'maintenance', templateId]);
        break;
      default:
        break;
    }
  }

  private confirmNavigation(hasResetDirty: boolean = true): Observable<any> {
    if (this.form.createMode) {
      this.notificationService.error('msg_save_first');
      return EMPTY$;
    } else if (this.form.dirty) {
      const confirmation$ = this.dialogService.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));

      const yes$ = confirmation$.pipe(
        filter$(yesAnswer),
        switchMap$(() => this.completeFlow.run({
          value: this.form.submitValue,
          form: this.form,
          trigger: 'process',
        }).toFinalState()),
        filter$((s) => !!s.success),
        map$(() => Confirmation.Yes));

      const no$ = confirmation$.pipe(
        filter$(noAnswer),
        tap$(() => {
          if (hasResetDirty) {
            this.form.resetDirty();
          }
        }));

      return merge$(yes$, no$);
    } else {
      return of$(Confirmation.Yes);
    }
  }

  private addTasks(tasks: ScheduleTemplateTask[]): void {
    each(tasks, (task: ScheduleTemplateTask) => this.form.addTask(task));
  }

  private buildGridDefinition(layout: GridLayout): GridDefinition {
    const model = createDefaultGridModel<any>({
      editable: (this.formMode === FormMode.EditMode && this.hasScheduleTemplatePermission('EDIT'))
        || (this.formMode === FormMode.CreateMode && this.hasScheduleTemplatePermission('CREATE')),
      getId: getScheduleTaskId,
      getCellDef: scheduleTaskFieldState,
      command: (entity: ScheduleTemplateTask, name, value) =>
        this.form.setTaskField(entity, name, value),
    });

    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      layout,
      model,
      options: <any>{
        rowClassRules: {
          [ROW_GROUP_CLASS]: ({ data }: { data: ScheduleTemplateTask }) => data.hasChildren,
        },
      },
      columns: {
        taskNumber: {
          valueFormatter: code3Formatter,
        },
        type: {
          valueFormatter: dictionaryValueFormatter(this.scheduleTemplateTaskType),
        },
        predecessors: {
          valueFormatter: predecessorValueFormatter,
          sortable: false,
        },
        duration: {
          valueFormatter: cellValueFormatter<number>((duration) =>
            this.localizationBusinessService.formatHoursDuration(duration)),
        },
        notes: {
          valueFormatter: this.notesValueFormatter.bind(this),
        },
        isInSupervisorSchedule: {
          ...buildEditableCheckboxColumn(model.getCellAccessor('isInSupervisorSchedule')),
          hide: this.scheduleTemplateType !== ScheduleType.Construction,
        },
        isExcludeFromGanttChart: buildEditableCheckboxColumn(model.getCellAccessor('isExcludeFromGanttChart')),
      },
    });
  }

  private notesValueFormatter({ data: { todo, notes } }: ValueFormatterParams): string {
    const notesSize = size(notes);
    const todoSize = size(todo);

    return [
      (notesSize > 0 ? [notesSize, this.translationService.get('lbl_n')].join('') : []),
      (todoSize > 0 ? [todoSize, this.translationService.get('lbl_t')].join('') : []),
    ].join('');
  }

  private getPageTitle(type: ScheduleType, name: string, formMode: FormMode): string {
    if (formMode === FormMode.CreateMode) {
      return {
        [ScheduleType.Construction]: 'lbl_create_construction_sch_template',
        [ScheduleType.Sales]: 'lbl_create_sales_sch_template',
        [ScheduleType.PreConstruction]: 'lbl_create_pre_construction_sch_template',
        [ScheduleType.Maintenance]: 'lbl_create_maintenance_sch_template',
      }[type];
    } else {
      return {
        [ScheduleType.Construction]:
          formMode === FormMode.ViewMode
            ? `${this.translationService.get('lbl_construction_sch_template')}: ${name}`
            : `${this.translationService.get('lbl_edit_construction_sch_template')}: ${name}`,
        [ScheduleType.Sales]:
          formMode === FormMode.ViewMode
            ? `${this.translationService.get('lbl_sales_sch_template')}: ${name}`
            : `${this.translationService.get('lbl_edit_sales_sch_template')}: ${name}`,
        [ScheduleType.PreConstruction]:
          formMode === FormMode.ViewMode
            ? `${this.translationService.get('lbl_pre_construction_sch_template')}: ${name}`
            : `${this.translationService.get('lbl_edit_pre_construction_sch_template')}: ${name}`,
        [ScheduleType.Maintenance]:
          formMode === FormMode.ViewMode
            ? `${this.translationService.get('lbl_maintenance_sch_template')}: ${name}`
            : `${this.translationService.get('lbl_edit_maintenance_sch_template')}: ${name}`,
      }[type];
    }
  }

  private updatePageTitle(): void {
    this.pageTitle = this.getPageTitle(
      this.scheduleTemplateType,
      <string>getFormStateSnapshotValue(this.form.info)['name'],
      this.form.mode);
  }
}
